(ns ui.view.transaction-process.components.detail-panel
  (:require
   [common.localization :refer [tr]]
   [re-frame.core :as rf]
   [ui.components.button.view :as button]
   [ui.state.session.sub :as session-sub]
   [frontend.view.modals.delete-confirmation.view :as delete-confirmation-modal]
   [frontend.view.modals.entity-audit-trail.view :as audit-trail-modal]
   [frontend.view.modals.events :as modals]
   [ui.view.transaction-process.components.sub-entity-display :as sub-display]
   [ui.view.transaction-process.event :as event]
   [ui.view.transaction-process.modals.attestation.event :as attestation-event]
   [ui.view.transaction-process.modals.decision.event :as decision-event]
   [ui.view.transaction-process.modals.declaration.event :as declaration-event]
   [ui.view.transaction-process.sub :as sub]
   [ui.view.transaction-process.util :as tp-util]))

(defn- live-entity
  "Re-resolve a stored sub-entity snapshot against the live session. The peek
  stays open after its edit modal closes, so it must reflect the just-saved
  edits rather than the snapshot captured when the peek was opened. Falls back
  to the snapshot if the entity isn't (yet) in the session map."
  [snapshot]
  (or (when-let [id (:db/id snapshot)]
        @(rf/subscribe [::session-sub/entity id]))
      snapshot))

(defn- edit-button
  [{:keys [on-edit]}]
  [button/sm-secondary-hollow
   {:on-click on-edit}
   (tr :edit)])

(defn- audit-trail-button
  [{:keys [entity-id label]}]
  [button/sm-secondary-hollow
   {:on-click (fn []
                (modals/open-modal!
                 audit-trail-modal/modal-key
                 {:title      (tr :audit-trail-for [label])
                  :entity-ids [entity-id]}))}
   (tr :audit-trail)])

(defn- danger-zone
  [{:keys [label help-text confirm-body process-id field-kw entity-id on-delete-success]}]
  [:div {:class "mt-6 pt-4 border-t border-gray-200"}
   [:span {:class "text-xs m-0 font-medium text-red-600 block mb-1"} (tr :danger-zone)]
   [:p {:class "text-xs m-0 text-gray-500 mb-2"} help-text]
   [button/sm-danger-solid
    {:on-click (fn []
                 (modals/open-modal!
                  delete-confirmation-modal/modal-key
                  {:body       (or confirm-body label)
                   :on-delete! #(rf/dispatch [::event/delete-sub-entity
                                              {:process-id process-id
                                               :field-kw   field-kw
                                               :entity-id  entity-id
                                               :on-success on-delete-success}])}))}
    label]])

(defn- decision-panel
  [{:keys [ctx item on-close on-edit can-write? can-delete? process-id]}]
  (let [decision (live-entity (:decision-entity item))
        template @(rf/subscribe [::sub/template ctx])
        slot-template (get-in item [:entry :slot/template])]
    (if-not decision
      [:div {:class "text-sm m-0 text-gray-400 italic"} (tr :transaction-process/no-decision-data)]
      [:div
       [:div {:class "flex items-center gap-2 mb-4"}
        (when on-edit
          [edit-button {:on-edit on-edit}])
        [audit-trail-button {:entity-id (:db/id decision)
                             :label     (or (:label item) (tr :decisions))}]]
       [:div {:class "mb-3"}
        [:span {:class "text-xs m-0 font-medium text-gray-500 block"} (tr :status)]
        (let [draft? (:decision/draft? decision)]
          [tp-util/status-badge {:label   (if draft? (tr :draft) (tr :logged))
                                 :variant (if draft? :warning :success)
                                 :class   "mt-1 inline-block"}])]
       (sub-display/value-field-rows {:slot-template slot-template :category :decisions
                                      :entity decision :template template})
       [sub-display/conditions-list {:entity           decision
                                     :conditions-field :decision/conditions
                                     :can-write?       can-write?
                                     :show-status?     false}]
       [sub-display/participants-list {:decision decision :field-configs (:template/fields template)}]
       [sub-display/reservations-list {:template template :decision decision}]
       [sub-display/attachments-list decision]
       (when can-delete?
         [danger-zone {:label             (tr :transaction-process/delete-decision)
                       :help-text         (tr :transaction-process/delete-decision-help)
                       :confirm-body      (tr :transaction-process/delete-decision-confirm)
                       :process-id        process-id
                       :field-kw          (:slot-kw item)
                       :entity-id         (:db/id decision)
                       :on-delete-success on-close}])])))

(defn- verification-panel
  [{:keys [ctx item on-close on-edit can-write-condition? can-delete? process-id]}]
  (let [attestation (live-entity (:attestation-entity item))
        template    @(rf/subscribe [::sub/template ctx])
        entry       (:entry item)
        slot-template (:slot/template entry)]
    (if-not attestation
      [:div {:class "text-sm m-0 text-gray-400 italic"} (tr :transaction-process/no-verification-data)]
      [:div
       [:div {:class "flex items-center gap-2 mb-4"}
        (when on-edit
          [edit-button {:on-edit on-edit}])
        [audit-trail-button {:entity-id (:db/id attestation)
                             :label     (or (:label item) (tr :verifications))}]]
       (when (:attestation/draft? attestation)
         [:div {:class "mb-3"}
          [:span {:class "text-xs m-0 font-medium text-gray-500 block"} (tr :status)]
          [tp-util/status-badge {:label (tr :draft) :variant :warning :class "mt-1 inline-block"}]])
       [:div {:class "mb-3"}
        [:span {:class "text-xs m-0 font-medium text-gray-500 block"} (tr :outcome)]
        [:div {:class "mt-1"}
         (let [outcome (:attestation/outcome attestation)]
           (cond
             (nil? outcome)
             [tp-util/status-badge {:label (tr :not-started) :variant :neutral}]

             (tp-util/attestation-display-label entry attestation)
             [tp-util/status-badge {:label   (tp-util/attestation-display-label entry attestation)
                                    :variant (tp-util/attestation-outcome-variant entry outcome)}]

             :else
             [tp-util/render-error {:context "Unmapped :attestation/outcome keyword"
                                    :value outcome}]))]]
       (sub-display/value-field-rows {:slot-template slot-template :category :attestations
                                      :entity attestation :template template})
       [sub-display/conditions-list {:entity           attestation
                                     :conditions-field :attestation/conditions
                                     :can-write?       can-write-condition?
                                     :show-status?     true}]
       [sub-display/attachments-list attestation]
       (when can-delete?
         [danger-zone {:label             (tr :transaction-process/delete-verification)
                       :help-text         (tr :transaction-process/delete-verification-help)
                       :confirm-body      (tr :transaction-process/delete-verification-confirm)
                       :process-id        process-id
                       :field-kw          (:slot-kw item)
                       :entity-id         (:db/id attestation)
                       :on-delete-success on-close}])])))

(defn- declaration-panel
  [{:keys [ctx item on-close on-edit can-delete? process-id]}]
  (let [declaration (live-entity (:declaration-entity item))
        template    @(rf/subscribe [::sub/template ctx])
        slot-template (get-in item [:entry :slot/template])]
    (if-not declaration
      [:div {:class "text-sm m-0 text-gray-400 italic"} (tr :transaction-process/no-declaration-data)]
      [:div
       [:div {:class "flex items-center gap-2 mb-4"}
        (when on-edit
          [edit-button {:on-edit on-edit}])
        [audit-trail-button {:entity-id (:db/id declaration)
                             :label     (or (:label item) (tr :declarations))}]]
       [:div {:class "mb-3"}
        [:span {:class "text-xs m-0 font-medium text-gray-500 block"} (tr :status)]
        (let [[label variant] (cond
                                (:declaration/draft? declaration)
                                [(tr :draft) :warning]
                                (:declaration/not-applicable? declaration)
                                [(tr :not-applicable) :neutral]
                                (:declaration/completed? declaration)
                                [(tr :completed) :success]
                                :else
                                [(tr :pending) :warning])]
          [tp-util/status-badge {:label label :variant variant :class "mt-1 inline-block"}])]
       (sub-display/value-field-rows {:slot-template slot-template :category :declarations
                                      :entity declaration :template template})
       (when can-delete?
         [danger-zone {:label             (tr :transaction-process/delete-declaration)
                       :help-text         (tr :transaction-process/delete-declaration-help)
                       :confirm-body      (tr :transaction-process/delete-declaration-confirm)
                       :process-id        process-id
                       :field-kw          (:slot-kw item)
                       :entity-id         (:db/id declaration)
                       :on-delete-success on-close}])])))

(defn view
  [{:keys [ctx]}]
  (let [{:keys [panel-type item] :as panel} @(rf/subscribe [::sub/detail-panel ctx])
        process-id (:process-id ctx)
        on-close #(rf/dispatch [::event/close-detail-panel ctx])
        can-write? (case panel-type
                     :verification @(rf/subscribe [::sub/can-write-field? ctx :attestation/outcome])
                     :decision     @(rf/subscribe [::sub/can-write-field? ctx :decision/date])
                     :declaration  @(rf/subscribe [::sub/can-write-field? ctx :declaration/completed?])
                     false)
        can-write-condition? (and (= :verification panel-type)
                                  @(rf/subscribe [::sub/can-write-field?
                                                  ctx
                                                  :attestation-condition/status]))
        ;; Sub-entities are deletable wherever they're editable: if the user has
        ;; write access to the entity's key field, they can also remove it.
        can-delete? can-write?]
    (when panel
      [:div {:class "w-72 xl:w-96 shrink-0 bg-white border-l border-gray-200 overflow-auto p-4"}
       [:div {:class "flex items-center justify-between mb-4"}
        [:h3 {:class "text-sm m-0 font-semibold text-gray-900"}
         (or (:label item) (tr :details))]
        [:button {:class    "text-gray-400 hover:text-gray-600 text-lg m-0 leading-none"
                  :on-click on-close}
         [:i {:class "fal fa-times"}]]]
       (case panel-type
         :verification [verification-panel
                        {:ctx          ctx
                         :item         item
                         :on-close     on-close
                         :can-write-condition? can-write-condition?
                         :can-delete?  can-delete?
                         :process-id   process-id
                         :on-edit      (when can-write?
                                         (fn []
                                           (rf/dispatch [::attestation-event/open ctx
                                                         {:slot-kw                 (:slot-kw item)
                                                          :attestation-type-label  (:label item)
                                                          :existing-attestation    (:attestation-entity item)}])))}]
         :decision     [decision-panel
                        {:ctx          ctx
                         :item         item
                         :on-close     on-close
                         :can-write?   can-write?
                         :can-delete?  can-delete?
                         :process-id   process-id
                         :on-edit      (when can-write?
                                         (fn []
                                           (rf/dispatch [::decision-event/open ctx
                                                         {:entity-type          (get-in item [:entry :slot/entity-type])
                                                          :slot-kw              (:slot-kw item)
                                                          :decision-type-label  (:label item)
                                                          :existing-decision    (:decision-entity item)}])))}]
         :declaration  [declaration-panel
                        {:ctx          ctx
                         :item         item
                         :on-close     on-close
                         :can-write?   can-write?
                         :can-delete?  can-delete?
                         :process-id   process-id
                         :on-edit      (when can-write?
                                         (fn []
                                           (rf/dispatch [::declaration-event/open ctx
                                                         {:slot-kw                 (:slot-kw item)
                                                          :declaration-type-label  (:label item)
                                                          :existing-declaration    (:declaration-entity item)}])))}]
         [:div {:class "text-sm m-0 text-gray-400 italic"} (tr :transaction-process/no-details-available)])])))
